import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Eye, Share2, Clock, CheckCircle2, Users, FolderOpen, Download, Loader2, ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import ShareAssessmentDialog from './ShareAssessmentDialog';
import { getAllPremiumProgress } from '@/services/premiumProgressService';
import { generateFreeSurveyPDF } from '@/utils/freeSurveyPdfGenerator';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
interface MyAssessment {
  id: string;
  attemptId?: string;
  assessmentId: string;
  assessmentTitle: string;
  assessmentSlug: string;
  status: 'in_progress' | 'completed' | 'shared';
  submittedAt?: string;
  updatedAt?: string;
  sharedByName?: string;
  sharedByEmail?: string;
  responseId?: string;
}

interface MyAssessmentsSectionProps {
  onContinueAssessment: (assessmentId: string, slug: string, attemptId?: string) => void;
  onViewResults: (assessmentId: string, slug: string, responseId?: string) => void;
}

export default function MyAssessmentsSection({ onContinueAssessment, onViewResults }: MyAssessmentsSectionProps) {
  const [assessments, setAssessments] = useState<MyAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState<MyAssessment | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { toast } = useToast();


  useEffect(() => {
    fetchMyAssessments();
  }, []);

  const fetchMyAssessments = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const results: MyAssessment[] = [];
      

      // 1. Fetch completed premium responses by email history, not only current auth user_id
      const { data: myResponses } = await supabase.functions.invoke('get-my-responses');
      const premiumResponses = (Array.isArray(myResponses) ? myResponses : []).filter((response: any) => Boolean(response.userId));

      // Show ALL completed premium responses (each attempt is independent)
      for (const response of premiumResponses) {
        
        results.push({
          id: `response-${response.id}`,
          assessmentId: response.assessmentId,
          assessmentTitle: response.assessmentTitle,
          assessmentSlug: response.assessmentSlug,
          status: 'completed',
          submittedAt: response.submittedAt,
          responseId: response.id,
        });
      }

      // 2. Fetch shared assessments (shared with me) using raw query to avoid type issues
      try {
        const { data: shares, error: sharesError } = await (supabase as any)
          .from('assessment_shares')
          .select('id, assessment_id, shared_by_name, shared_by_email, created_at')
          .or(`shared_with_user_id.eq.${user.id},shared_with_email.eq.${user.email}`);

        if (!sharesError && shares && shares.length > 0) {
          const shareAssessmentIds = [...new Set(shares.map((s: any) => s.assessment_id))];
          const { data: shareAssessmentData } = await supabase
            .from('assessments')
            .select('id, title, slug')
            .in('id', shareAssessmentIds as string[]);

          const shareAssessmentMap = new Map(shareAssessmentData?.map(a => [a.id, a]) || []);

          for (const share of shares) {
            const assessment = shareAssessmentMap.get(share.assessment_id);
            if (assessment) {
              const alreadyExists = results.some(r => r.assessmentId === assessment.id);
              if (!alreadyExists) {
                results.push({
                  id: `share-${share.id}`,
                  assessmentId: assessment.id,
                  assessmentTitle: assessment.title,
                  assessmentSlug: assessment.slug,
                  status: 'shared',
                  sharedByName: share.shared_by_name || share.shared_by_email || 'Someone',
                  sharedByEmail: share.shared_by_email || undefined,
                });
              }
            }
          }
        }
      } catch (err) {
        console.warn('Could not fetch assessment shares:', err);
      }

      // 3. Check DB for ongoing premium assessments
      try {
        const dbProgress = await getAllPremiumProgress();
        for (const progress of dbProgress) {
          if (progress.responses && Object.keys(progress.responses).length > 0) {
            const { data: assessmentInfo } = await supabase
              .from('assessments')
              .select('id, title, slug')
              .eq('id', progress.assessment_id)
              .maybeSingle();

            if (assessmentInfo) {
              results.push({
                id: `ongoing-${progress.id}`,
                attemptId: progress.id,
                assessmentId: assessmentInfo.id,
                assessmentTitle: progress.custom_name?.trim() || assessmentInfo.title,
                assessmentSlug: assessmentInfo.slug,
                status: 'in_progress',
                updatedAt: progress.updated_at,
              });
            }
          }
        }
      } catch (err) {
        console.warn('Could not fetch DB progress:', err);
      }

      // 4. Also check localStorage for ongoing assessments (fallback)
      const { data: allAssessments } = await supabase
        .from('assessments')
        .select('id, title, slug')
        .eq('is_active', true);

      if (allAssessments) {
        for (const assessment of allAssessments) {
          const stored = localStorage.getItem(`assessment_${assessment.id}_responses`);
          const storedProgress = localStorage.getItem(`assessment-progress-${assessment.id}`);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              const parsedProgress = storedProgress ? JSON.parse(storedProgress) : null;
              const isCompletedLocally = parsedProgress?.step === 'complete';

              if (Object.keys(parsed).length > 0 && !isCompletedLocally) {
                const alreadyInProgress = results.some(r => r.assessmentId === assessment.id && r.status === 'in_progress');
                if (!alreadyInProgress) {
                  results.push({
                    id: `ongoing-${assessment.id}`,
                    assessmentId: assessment.id,
                    assessmentTitle: assessment.title,
                    assessmentSlug: assessment.slug,
                    status: 'in_progress',
                  });
                }
              }
            } catch { /* ignore */ }
          }
        }
      }

      setAssessments(results);
    } catch (err) {
      console.error('Error fetching my assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (item: MyAssessment) => {
    if (!item.responseId) return;
    setDownloadingId(item.id);
    try {
      // Use the edge function to fetch response data (bypasses RLS restrictions)
      const { data: myResponses } = await supabase.functions.invoke('get-my-responses');
      const allResponses = Array.isArray(myResponses) ? myResponses : [];
      const matchedResponse = allResponses.find((r: any) => r.id === item.responseId);
      if (!matchedResponse) throw new Error('Response not found');

      const scoresByCategory = matchedResponse.scoresByCategory || {};
      const maturityScores = matchedResponse.maturityScores || {};
      const answerDetails = matchedResponse.answerDetails || {};

      // Fetch assessment categories for recommendation generation
      const { data: categories } = await supabase
        .from('categories')
        .select('id, name, description, order_index')
        .eq('assessment_id', item.assessmentId)
        .order('order_index');

      let enrichedCategories: any[] = [];
      if (categories && categories.length > 0) {
        const catIds = categories.map(c => c.id);
        const { data: questions } = await supabase
          .from('questions')
          .select('id, text, answer_type, category_id, order_index')
          .in('category_id', catIds)
          .order('order_index');
        const questionIds = questions?.map(q => q.id) || [];
        const { data: answers } = await supabase
          .from('answers')
          .select('id, text, value, question_id, recommended_actions')
          .in('question_id', questionIds);

        enrichedCategories = categories.map(cat => ({
          ...cat,
          questions: (questions || []).filter(q => q.category_id === cat.id).map(q => ({
            id: q.id,
            text: q.text,
            answerType: q.answer_type,
            answers: (answers || []).filter(a => a.question_id === q.id).map(a => ({
              id: a.id,
              text: a.text,
              value: a.value,
              recommendedActions: Array.isArray(a.recommended_actions) ? a.recommended_actions : [],
            })),
          })),
        }));
      }

      // Fetch assessment info for is_maturity_model
      const { data: assessmentInfo } = await supabase
        .from('assessments')
        .select('is_maturity_model')
        .eq('id', item.assessmentId)
        .maybeSingle();

      await generateFreeSurveyPDF({
        assessmentTitle: item.assessmentTitle,
        isMaturityModel: assessmentInfo?.is_maturity_model || false,
        scoresByCategory,
        maturityScores,
        answerDetails,
        categories: enrichedCategories,
        submittedAt: item.submittedAt || new Date().toISOString(),
        isPremiumReport: true,
      });
    } catch (err) {
      console.error('Download report error:', err);
      toast({ variant: 'destructive', title: 'Download failed', description: 'Could not generate the report.' });
    } finally {
      setDownloadingId(null);
    }
  };

  const VISIBLE_COUNT = 3;
  const [activeTab, setActiveTab] = useState<'completed' | 'in_progress'>('completed');

  const completedItems = assessments.filter(a => a.status === 'completed' || a.status === 'shared');
  const inProgressItems = assessments.filter(a => a.status === 'in_progress');

  const currentList = activeTab === 'completed' ? completedItems : inProgressItems;
  const visibleAssessments = showAll ? currentList : currentList.slice(0, VISIBLE_COUNT);
  const hasMore = currentList.length > VISIBLE_COUNT;

  const renderItem = (item: MyAssessment) => (
    <Card key={item.id} className="border border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-4 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-foreground truncate">{item.assessmentTitle}</h3>
            {item.status === 'shared' && (
              <Badge variant="outline" className="text-xs gap-1 border-blue-300 text-blue-600 bg-blue-50">
                <Users className="h-3 w-3" /> Shared
              </Badge>
            )}
          </div>
          {item.submittedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated {format(new Date(item.submittedAt), 'MMM dd, yyyy • h:mm a')}
            </p>
          )}
          {item.updatedAt && !item.submittedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated {format(new Date(item.updatedAt), 'MMM dd, yyyy • h:mm a')}
            </p>
          )}
          {item.sharedByName && (
            <p className="text-xs text-muted-foreground mt-1">
              Shared by <span className="font-medium">{item.sharedByName}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {item.status === 'in_progress' && (
            <Button
              size="sm"
              onClick={() => onContinueAssessment(item.assessmentId, item.assessmentSlug, item.attemptId)}
              className="gap-1"
            >
              <Play className="h-3.5 w-3.5" /> Continue
            </Button>
          )}
          {item.status === 'completed' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewResults(item.assessmentId, item.assessmentSlug, item.responseId)}
                className="gap-1"
              >
                <Eye className="h-3.5 w-3.5" /> View Results
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadReport(item)}
                disabled={downloadingId === item.id}
                className="gap-1"
              >
                {downloadingId === item.id ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Downloading...</>
                ) : (
                  <><Download className="h-3.5 w-3.5" /> Report</>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShareTarget(item)}
                className="gap-1"
              >
                <Share2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {item.status === 'shared' && (
            <Button
              size="sm"
              onClick={() => onContinueAssessment(item.assessmentId, item.assessmentSlug)}
              className="gap-1"
            >
              <Eye className="h-3.5 w-3.5" /> Open
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-foreground mb-4">My Assessments</h2>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading your assessments...</p>
      ) : assessments.length === 0 ? (
        <Card className="border border-dashed border-border">
          <CardContent className="p-8 flex flex-col items-center text-center">
            <FolderOpen className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              No assessments yet. Start a full assessment to see it here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as any); setShowAll(false); }}>
          <TabsList className="grid grid-cols-2 w-full max-w-md mb-4 h-auto bg-muted/50 p-1">
            <TabsTrigger
              value="completed"
              className="flex items-center gap-2 py-2.5 transition-all duration-200 ease-out hover:bg-orange-100 hover:text-orange-700 hover:scale-[1.03]"
            >
              <CheckCircle2 className="h-4 w-4" />
              <span>Completed</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{completedItems.length}</Badge>
            </TabsTrigger>
            <TabsTrigger
              value="in_progress"
              className="flex items-center gap-2 py-2.5 transition-all duration-200 ease-out hover:bg-orange-100 hover:text-orange-700 hover:scale-[1.03]"
            >
              <PlayCircle className="h-4 w-4" />
              <span>Ongoing</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{inProgressItems.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-3 mt-0">
            {currentList.length === 0 ? (
              <Card className="border border-dashed border-border">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <FolderOpen className="h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'completed'
                      ? 'No completed assessments yet.'
                      : 'No ongoing assessments.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {visibleAssessments.map(renderItem)}
                {hasMore && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                    className="w-full text-muted-foreground hover:text-foreground gap-1"
                  >
                    {showAll ? (
                      <><ChevronUp className="h-4 w-4" /> Show Less</>
                    ) : (
                      <><ChevronDown className="h-4 w-4" /> View Older ({currentList.length - VISIBLE_COUNT} more)</>
                    )}
                  </Button>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      )}

      {shareTarget && (
        <ShareAssessmentDialog
          open={!!shareTarget}
          onOpenChange={(v) => { if (!v) setShareTarget(null); }}
          assessmentId={shareTarget.assessmentId}
          assessmentTitle={shareTarget.assessmentTitle}
          assessmentSlug={shareTarget.assessmentSlug}
        />
      )}
    </div>
  );
}
